import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { supabase } from "./supabaseClient";
import Nav from "./Nav";

const NAV_LINKS = [
  { label: "Home", href: "/", external: false },
  { label: "Hierarchy", href: "/hierarchy", external: false },
  { label: "Events", href: "/events", external: false },
];

const MEMBER_ROLE_ID = "1303561707196649512";

// "Nap Time" voice channel — idle/AFK time, excluded from all VC hour/activity
// stats across the app. Matched loosely (casing/spacing-insensitive).
function isNaptimeChannel(name) {
  return (name || "").replace(/\s+/g, "").toLowerCase() === "naptime";
}

const DIVISION_META = {
  Industry: {
    icon: "⛏",
    color: "#fcd657",
    tagline: "We Mine It.",
    desc: "Extracting value from the 'verse — rock by rock. Our mining and salvage teams keep the supply lines rich.",
  },
  Security: {
    icon: "🛡",
    color: "#ef5350",
    tagline: "We (Probably) Blow It.",
    desc: "Ground squads, fighter wings, medics, and capital-class muscle. We don't start fights. We end them.",
  },
};

function slugify(name) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function StarField() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    const stars = Array.from({ length: 350 }, () => ({
      x: Math.random(),
      y: Math.random(),
      r: Math.random() * 1.8 + 0.3,
      a: Math.random(),
      speed: Math.random() * 0.004 + 0.001,
    }));
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      stars.forEach((s) => {
        s.a += s.speed;
        if (s.a > 1) s.a = 0;
        const alpha = Math.sin(s.a * Math.PI) * 0.85 + 0.15;
        ctx.beginPath();
        ctx.arc(s.x * canvas.width, s.y * canvas.height, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${alpha})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);
  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed", top: 0, left: 0,
        width: "100%", height: "100vh",
        pointerEvents: "none", zIndex: 0,
      }}
    />
  );
}

function useScrollReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function RevealSection({ children, delay = 0, style = {} }) {
  const [ref, visible] = useScrollReveal();
  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(40px)",
        transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function CountUpStat({ stat, delay = 0 }) {
  const [ref, visible] = useScrollReveal();
  const [display, setDisplay] = useState("0");
  const [glowing, setGlowing] = useState(false);
  const hasStarted = useRef(false);

  useEffect(() => {
    if (!visible || hasStarted.current) return;
    hasStarted.current = true;

    // Special case: ∞ — scramble effect
    if (stat.special === "∞") {
      const chars = ["0", "1", "8", "∞", "§", "∞", "8", "∞", "§", "∞", "8", "∞", "§", "∞"];
      let i = 0;
      const scramble = setInterval(() => {
        setDisplay(chars[i % chars.length]);
        i++;
        if (i >= chars.length) {
          clearInterval(scramble);
          setDisplay("∞");
          setGlowing(true);
          setTimeout(() => setGlowing(false), 800);
        }
      }, 120);
      return;
    }

    // Special case: 0 — counts up dramatically then slams back to 0
    if (stat.countTo === 0) {
      const peak = 47;
      const duration = 3200;
      const steps = 60;
      let step = 0;
      const interval = setInterval(() => {
        step++;
        const progress = step / steps;
        if (progress < 0.75) {
          const n = Math.round(peak * (progress / 0.75));
          setDisplay(String(n));
        } else {
          const crashProgress = (progress - 0.75) / 0.25;
          const n = Math.round(peak * (1 - crashProgress));
          setDisplay(String(Math.max(0, n)));
        }
        if (step >= steps) {
          clearInterval(interval);
          setDisplay("0");
          setGlowing(true);
          setTimeout(() => setGlowing(false), 800);
        }
      }, duration / steps);
      return;
    }

    // Normal count up with easing
    const target = stat.countTo;
    const duration = 3000;
    const steps = 90;
    let step = 0;
    const interval = setInterval(() => {
      step++;
      const progress = step / steps;
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(eased * target);
      setDisplay(String(current));
      if (step >= steps) {
        clearInterval(interval);
        setDisplay(String(target));
        setGlowing(true);
        setTimeout(() => setGlowing(false), 600);
      }
    }, duration / steps);
  }, [visible, stat]);

  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(40px)",
      transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
      textAlign: "center",
    }}>
      <div style={{
        fontFamily: "'Orbitron', sans-serif",
        fontSize: "clamp(36px, 5vw, 60px)",
        fontWeight: 900,
        color: "#fcd657",
        lineHeight: 1,
        transition: "text-shadow 0.3s ease",
        textShadow: glowing
          ? "0 0 20px rgba(252,214,87,0.9), 0 0 60px rgba(252,214,87,0.5)"
          : "0 0 8px rgba(252,214,87,0.2)",
      }}>
        {stat.prefix}{display}{stat.suffix}
      </div>
      <div style={{
        fontFamily: "'Space Mono', monospace",
        fontSize: 10, letterSpacing: 3,
        color: "rgba(232,234,246,0.4)", marginTop: 8, textTransform: "uppercase",
      }}>{stat.label}</div>
    </div>
  );
}

// ─── Active Hours Heatmap (recruitment-facing) ───────────────────────────────
// Pulls the org's last 90 days of voice activity, buckets it by day-of-week × hour
// in whatever timezone the visitor selects, and renders a clean 7×24 gold grid.
// Default timezone is the visitor's browser TZ; the dropdown lets them shift the
// grid to compare against their own schedule.
const TZ_PRESETS = [
  { tz: "America/Los_Angeles", label: "Los Angeles · Pacific" },
  { tz: "America/Denver",      label: "Denver · Mountain" },
  { tz: "America/Chicago",     label: "Chicago · Central" },
  { tz: "America/New_York",    label: "New York · Eastern" },
  { tz: "America/Toronto",     label: "Toronto · Eastern" },
  { tz: "America/Anchorage",   label: "Anchorage · Alaska" },
  { tz: "Pacific/Honolulu",    label: "Honolulu · Hawaii" },
  { tz: "Europe/London",       label: "London · GMT/BST" },
  { tz: "Europe/Paris",        label: "Paris · CET/CEST" },
  { tz: "Europe/Berlin",       label: "Berlin · CET/CEST" },
  { tz: "Europe/Athens",       label: "Athens · EET/EEST" },
  { tz: "Asia/Kolkata",        label: "India · IST" },
  { tz: "Asia/Shanghai",       label: "Shanghai · China" },
  { tz: "Asia/Tokyo",          label: "Tokyo · Japan" },
  { tz: "Australia/Sydney",    label: "Sydney · AEST/AEDT" },
  { tz: "UTC",                 label: "UTC" },
];

function ActiveHoursHeatmap() {
  const browserTz = (typeof Intl !== "undefined") ? Intl.DateTimeFormat().resolvedOptions().timeZone : "UTC";
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tz, setTz] = useState(browserTz);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const since = new Date();
        since.setDate(since.getDate() - 90);
        const { data, error: err } = await supabase
          .from("voice_activity_events")
          .select("event_at, session_seconds, channel_name")
          .eq("action", "left")
          .not("session_seconds", "is", null)
          .gte("event_at", since.toISOString())
          .limit(50000);
        if (cancelled) return;
        if (err) { setError(true); setLoading(false); return; }
        setEvents(data || []);
        setLoading(false);
      } catch {
        if (!cancelled) { setError(true); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Bucket events by the selected timezone's local DOW + hour.
  const { grid, max, totalHours, totalSessions, peakDay, peakHour } = useMemo(() => {
    const g = Array.from({ length: 7 }, () => new Array(24).fill(0));
    if (events.length === 0) return { grid: g, max: 0, totalHours: 0, totalSessions: 0, peakDay: null, peakHour: null };
    let fmt;
    try {
      fmt = new Intl.DateTimeFormat("en-US", { timeZone: tz, weekday: "short", hour: "numeric", hour12: false });
    } catch { fmt = null; }
    const dowMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    let totalSec = 0;
    let sessionCount = 0;
    events.forEach(ev => {
      if (isNaptimeChannel(ev.channel_name)) return; // exclude idle/AFK nap time
      const d = new Date(ev.event_at);
      let dow = d.getDay();
      let hour = d.getHours();
      if (fmt) {
        const parts = fmt.formatToParts(d);
        parts.forEach(p => {
          if (p.type === "weekday" && dowMap[p.value] !== undefined) dow = dowMap[p.value];
          if (p.type === "hour") {
            const v = parseInt(p.value, 10);
            if (!Number.isNaN(v)) hour = v % 24;
          }
        });
      }
      const sec = ev.session_seconds || 0;
      g[dow][hour] += sec;
      totalSec += sec;
      sessionCount++;
    });
    let mx = 0, pd = null, ph = null;
    g.forEach((row, di) => row.forEach((v, hi) => { if (v > mx) { mx = v; pd = di; ph = hi; } }));
    return { grid: g, max: mx, totalHours: totalSec / 3600, totalSessions: sessionCount, peakDay: pd, peakHour: ph };
  }, [events, tz]);

  function fmtHr(h) { return h === 0 ? "12am" : h === 12 ? "12pm" : h < 12 ? `${h}am` : `${h - 12}pm`; }
  const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Make sure the visitor's actual TZ shows up in the dropdown even if it's not in our preset list.
  const tzOptions = TZ_PRESETS.some(p => p.tz === browserTz)
    ? TZ_PRESETS
    : [{ tz: browserTz, label: `${browserTz.replace(/_/g, " ")} (your timezone)` }, ...TZ_PRESETS];

  return (
    <div style={{
      padding: "clamp(20px, 3vw, 32px)",
      border: "1px solid rgba(252,214,87,0.18)",
      background: "linear-gradient(180deg, rgba(252,214,87,0.03) 0%, rgba(255,255,255,0.01) 100%)",
      clipPath: "polygon(0 0, calc(100% - 16px) 0, 100% 16px, 100% 100%, 16px 100%, 0 calc(100% - 16px))",
    }}>
      {/* Header — TZ selector + summary stats */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16, marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "rgba(232,234,246,0.5)", textTransform: "uppercase" }}>Show times in</span>
          <div style={{ position: "relative", display: "inline-block" }}>
            <select value={tz} onChange={e => setTz(e.target.value)} style={{
              appearance: "none", WebkitAppearance: "none", MozAppearance: "none",
              fontFamily: "'Orbitron', sans-serif", fontSize: 11, fontWeight: 700,
              letterSpacing: 1.5, textTransform: "uppercase",
              background: "rgba(252,214,87,0.12)", color: "#fcd657",
              border: "1px solid #fcd657",
              padding: "8px 32px 8px 14px", cursor: "pointer",
            }}>
              {tzOptions.map(opt => (
                <option key={opt.tz} value={opt.tz} style={{ background: "#05070f", color: "#e8eaf6" }}>{opt.label}</option>
              ))}
            </select>
            <span aria-hidden style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "#fcd657", fontSize: 10 }}>▼</span>
          </div>
        </div>
        {!loading && !error && totalSessions > 0 && (
          <div style={{ display: "flex", gap: 18, fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: "rgba(232,234,246,0.55)", textTransform: "uppercase", flexWrap: "wrap" }}>
            <span><span style={{ color: "#fcd657", fontWeight: 700 }}>{Math.round(totalHours).toLocaleString()}h</span> in voice · 90 days</span>
            <span><span style={{ color: "#fcd657", fontWeight: 700 }}>{totalSessions.toLocaleString()}</span> sessions</span>
            {peakDay !== null && (
              <span>peak: <span style={{ color: "#fcd657", fontWeight: 700 }}>{DAY_LABELS[peakDay]} {fmtHr(peakHour)}</span></span>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ padding: 60, textAlign: "center", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: 2, color: "rgba(252,214,87,0.5)", textTransform: "uppercase" }}>Loading…</div>
      ) : error || events.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: 2, color: "rgba(232,234,246,0.3)", textTransform: "uppercase", border: "1px dashed rgba(255,255,255,0.06)" }}>
          No voice activity data yet
        </div>
      ) : (
        <>
          {/* Heatmap grid */}
          <div style={{ overflowX: "auto" }}>
            <div style={{ display: "grid", gridTemplateColumns: "44px repeat(24, minmax(20px, 1fr))", gap: 3, minWidth: 540 }}>
              {/* Hour header row */}
              <div></div>
              {Array.from({ length: 24 }, (_, h) => (
                <div key={h} style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.45)", textAlign: "center", paddingBottom: 4 }}>
                  {h % 3 === 0 ? fmtHr(h).replace("am", "a").replace("pm", "p") : ""}
                </div>
              ))}
              {/* Rows */}
              {DAY_LABELS.map((label, di) => (
                <React.Fragment key={di}>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1, color: "rgba(232,234,246,0.55)", display: "flex", alignItems: "center", paddingRight: 8, textTransform: "uppercase" }}>{label}</div>
                  {Array.from({ length: 24 }, (_, h) => {
                    const v = grid[di][h];
                    const intensity = max > 0 ? v / max : 0;
                    const isPeak = di === peakDay && h === peakHour && max > 0;
                    return (
                      <div key={h} title={`${label} ${fmtHr(h)} — ${(v / 3600).toFixed(1)}h`} style={{
                        aspectRatio: "1",
                        background: intensity > 0 ? `rgba(252,214,87,${0.07 + intensity * 0.86})` : "rgba(255,255,255,0.02)",
                        border: `1px solid ${isPeak ? "#fcd657" : "rgba(255,255,255,0.04)"}`,
                        boxShadow: isPeak ? "0 0 8px rgba(252,214,87,0.6)" : "none",
                        transition: "background 0.3s",
                      }} />
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>
          {/* Legend */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginTop: 18, paddingTop: 14, borderTop: "1px solid rgba(255,255,255,0.05)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: "rgba(232,234,246,0.45)", textTransform: "uppercase" }}>
              <span>Quieter</span>
              {[0, 0.15, 0.3, 0.5, 0.7, 0.9].map(i => (
                <div key={i} style={{ width: 18, height: 12, background: i === 0 ? "rgba(255,255,255,0.04)" : `rgba(252,214,87,${0.07 + i * 0.86})`, border: "1px solid rgba(255,255,255,0.06)" }} />
              ))}
              <span>Busier</span>
            </div>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: "rgba(232,234,246,0.4)", textTransform: "uppercase" }}>
              Hour of day · {tz.replace(/_/g, " ")}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function StarKeeperLanding() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [hoveredDiv, setHoveredDiv] = useState(null);
  const { user, profile, signInWithDiscord, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [divisions, setDivisions] = useState([]);

  // Membership check — mirrors App.js. member_status is the canonical source,
  // discord_roles is a snapshot that can lag (cached during the periodic
  // sync). Treat any of the three as sufficient.
  const roles = Array.isArray(profile?.discord_roles) ? profile.discord_roles : [];
  const hasMemberRole = profile?.member_status === 'member'
    || profile?.is_admin
    || roles.includes(MEMBER_ROLE_ID);

  // Auto-kick OAuth when a logged-out user lands here from a gated link
  // (ProtectedRoute / MemberRoute set ?redirect= when bouncing them). Skipped
  // once per browser tab via sessionStorage so a failed/canceled auth doesn't
  // loop the user back into Discord every refresh.
  useEffect(() => {
    if (authLoading) return;
    if (user) return;
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (!params.get('redirect')) return;
    if (sessionStorage.getItem('autoSignInTried') === '1') return;
    sessionStorage.setItem('autoSignInTried', '1');
    signInWithDiscord();
  }, [authLoading, user, signInWithDiscord]);

  // Once the user is logged in, clear the auto-sign-in flag so it can re-trigger
  // on a future logged-out visit to another gated link.
  useEffect(() => {
    if (user && typeof window !== 'undefined') {
      sessionStorage.removeItem('autoSignInTried');
    }
  }, [user]);

  useEffect(() => {
    async function loadLiveData() {
      const now = new Date().toISOString().slice(0, 10);
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const [{ data: members }, { data: teams }, { data: profileShips }, { data: events }, { data: vcRows }] = await Promise.all([
        supabase.from("profiles").select("id").limit(5000),
        supabase.from("teams").select("id, name, color, divisions, division").eq("archived", false).order("name"),
        supabase.from("profiles").select("ships").not("ships", "is", null).limit(5000),
        supabase.from("events").select("id, date, archived").gte("date", now),
        // VC hours over the last 30 days. session_seconds is logged on each
        // leave/move event; we sum and convert to hours for the landing tile.
        // Nap Time (idle/AFK) is excluded.
        supabase.from("voice_activity_events").select("session_seconds, channel_name").gte("event_at", thirtyDaysAgo).not("session_seconds", "is", null),
      ]);
      const vcHoursLast30d = Math.round(
        ((vcRows || [])
          .filter(r => !isNaptimeChannel(r.channel_name))
          .reduce((s, r) => s + (r.session_seconds || 0), 0)) / 3600
      );

      // Count unique ships owned by at least one member
      const ownedShipIds = new Set();
      (profileShips || []).forEach(p => {
        const userShips = Array.isArray(p.ships) ? p.ships : [];
        userShips.forEach(entry => {
          if (typeof entry === 'object' && entry !== null && entry.ship_id) ownedShipIds.add(entry.ship_id);
        });
      });

      setStats({
        members: (members || []).length,
        teams: (teams || []).length,
        ships: ownedShipIds.size,
        vcHoursLast30d,
      });

      // Build division data from teams
      const divMap = {};
      for (const t of (teams || [])) {
        const divs = Array.isArray(t.divisions) ? t.divisions : (t.division ? [t.division] : []);
        for (const d of divs) {
          if (!divMap[d]) divMap[d] = [];
          divMap[d].push(t);
        }
      }
      // Only show Industry and Security
      const divList = ["Industry", "Security"].map(name => ({
        name,
        ...(DIVISION_META[name] || { icon: "◆", color: "#ce93d8", tagline: "", desc: "" }),
        teams: divMap[name] || [],
      }));
      setDivisions(divList);
    }
    loadLiveData();
  }, []);

  return (
    <div style={{
      fontFamily: "'Rajdhani', 'Orbitron', sans-serif",
      background: "#05070f",
      color: "#e8eaf6",
      minHeight: "100vh",
      overflowX: "hidden",
    }}>
      <StarField />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@300;400;500;600;700&family=Orbitron:wght@400;600;700;900&family=Space+Mono&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::selection { background: #fcd657; color: #05070f; }
        a { text-decoration: none; color: inherit; }
        .glow-btn {
          display: inline-block;
          padding: 14px 36px;
          border: 2px solid #fcd657;
          color: #fcd657;
          font-family: 'Orbitron', sans-serif;
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 3px;
          text-transform: uppercase;
          cursor: pointer;
          position: relative;
          background: transparent;
          transition: all 0.3s ease;
          clip-path: polygon(12px 0%, 100% 0%, calc(100% - 12px) 100%, 0% 100%);
        }
        .glow-btn:hover {
          background: #fcd657;
          color: #05070f;
          box-shadow: 0 0 40px rgba(252,214,87,0.5);
        }
        .glow-btn-alt {
          display: inline-block;
          padding: 14px 36px;
          border: 2px solid rgba(255,255,255,0.3);
          color: #e8eaf6;
          font-family: 'Orbitron', sans-serif;
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 3px;
          text-transform: uppercase;
          cursor: pointer;
          background: transparent;
          transition: all 0.3s ease;
          clip-path: polygon(12px 0%, 100% 0%, calc(100% - 12px) 100%, 0% 100%);
        }
        .glow-btn-alt:hover {
          border-color: #e8eaf6;
          background: rgba(255,255,255,0.08);
        }
        .nav-link {
          font-family: 'Orbitron', sans-serif;
          font-size: 11px;
          letter-spacing: 2.5px;
          text-transform: uppercase;
          color: rgba(232,234,246,0.65);
          transition: color 0.2s;
          padding: 4px 0;
          position: relative;
        }
        .nav-link::after {
          content: '';
          position: absolute;
          bottom: 0; left: 0;
          width: 0; height: 1px;
          background: #fcd657;
          transition: width 0.3s ease;
        }
        .nav-link:hover { color: #fcd657; }
        .nav-link:hover::after { width: 100%; }
        .div-card {
          position: relative;
          border: 1px solid rgba(255,255,255,0.08);
          background: rgba(255,255,255,0.03);
          padding: 48px 36px;
          transition: all 0.4s ease;
          clip-path: polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 20px 100%, 0 calc(100% - 20px));
          cursor: default;
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        .div-card .div-teams { margin-top: auto; }
        .scanline {
          position: fixed;
          top: 0; left: 0;
          width: 100%; height: 100%;
          pointer-events: none;
          z-index: 9999;
          background: repeating-linear-gradient(
            0deg,
            transparent,
            transparent 2px,
            rgba(0,0,0,0.03) 2px,
            rgba(0,0,0,0.03) 4px
          );
        }
        @keyframes floatUp {
          from { opacity: 0; transform: translateY(60px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes lineExpand {
          from { width: 0; }
          to { width: 80px; }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        .hero-title span {
          display: block;
        }
        .stat-num {
          font-family: 'Orbitron', sans-serif;
          font-size: clamp(36px, 5vw, 60px);
          font-weight: 900;
          color: #fcd657;
          line-height: 1;
        }
        .corner-accent::before {
          content: '';
          position: absolute;
          top: 0; left: 0;
          width: 32px; height: 32px;
          border-top: 2px solid #fcd657;
          border-left: 2px solid #fcd657;
        }
        .corner-accent::after {
          content: '';
          position: absolute;
          bottom: 0; right: 0;
          width: 32px; height: 32px;
          border-bottom: 2px solid #fcd657;
          border-right: 2px solid #fcd657;
        }
      `}</style>

      {/* Scanline overlay */}
      <div className="scanline" />

      <Nav accentColor="#fcd657" />

      {/* HERO */}
      <section style={{
        position: "relative", minHeight: "100vh",
        display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden",
        padding: "clamp(80px, 15vw, 120px) clamp(16px, 4vw, 40px) clamp(60px, 10vw, 80px)",
      }}>

        {/* Radial gradient glow */}
        <div style={{
          position: "absolute", top: "40%", left: "50%",
          transform: "translate(-50%,-50%)",
          width: 800, height: 800,
          background: "radial-gradient(circle, rgba(252,214,87,0.08) 0%, transparent 70%)",
          pointerEvents: "none", zIndex: 1,
        }} />

        {/* Banner video — looping, muted, autoplay; banner.png is the poster so the first paint is instant */}
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="/images/banner.png"
          style={{
            position: "absolute", inset: 0,
            width: "100%", height: "100%",
            objectFit: "cover", objectPosition: "center",
            opacity: 0.18, zIndex: 0,
            pointerEvents: "none",
          }}
        >
          <source src="/videos/banner.mp4" type="video/mp4" />
        </video>

        <div style={{ position: "relative", zIndex: 2, maxWidth: 900, width: "100%", textAlign: "center" }}>
          {/* Label */}
          <div style={{
            fontFamily: "'Space Mono', monospace",
            fontSize: 11, letterSpacing: 5, color: "#fcd657",
            marginBottom: 24, opacity: 0,
            animation: "floatUp 0.6s ease forwards",
            animationDelay: "0.2s",
          }}>
            ◆ STAR CITIZEN ORGANIZATION ◆
          </div>

          {/* Title */}
          <h1 style={{
            fontFamily: "'Orbitron', sans-serif",
            fontWeight: 900,
            fontSize: "clamp(48px, 9vw, 110px)",
            lineHeight: 0.9,
            letterSpacing: "-1px",
            color: "#fcd657",
            marginBottom: 8,
            opacity: 0,
            animation: "floatUp 0.7s ease forwards",
            animationDelay: "0.4s",
          }}>
            STARKEEPER
          </h1>
          <h2 style={{
            fontFamily: "'Orbitron', sans-serif",
            fontWeight: 400,
            fontSize: "clamp(14px, 2.5vw, 22px)",
            letterSpacing: 8,
            color: "rgba(232,234,246,0.5)",
            marginBottom: 40,
            opacity: 0,
            animation: "floatUp 0.7s ease forwards",
            animationDelay: "0.55s",
          }}>
            INDUSTRIES
          </h2>

          {/* Tagline */}
          <p style={{
            fontFamily: "'Rajdhani', sans-serif",
            fontSize: "clamp(20px, 3.5vw, 32px)",
            fontWeight: 600,
            color: "#e8eaf6",
            marginBottom: 16,
            opacity: 0,
            animation: "floatUp 0.7s ease forwards",
            animationDelay: "0.7s",
          }}>
            We mine it. We move it. We{" "}
            <span style={{ color: "#ef5350" }}>
              (probably) blow it.
            </span>
          </p>

          <p style={{
            fontFamily: "'Rajdhani', sans-serif",
            fontSize: "clamp(15px, 2vw, 19px)",
            color: "rgba(232,234,246,0.55)",
            maxWidth: 580, margin: "0 auto 52px",
            lineHeight: 1.7,
            opacity: 0,
            animation: "floatUp 0.7s ease forwards",
            animationDelay: "0.85s",
          }}>
            A Star Citizen org built on teamwork without pressure, structure without rigidity,
            and fun above all else. Space is cold — but it doesn't have to be lonely.
          </p>

          <div style={{
            display: "flex", gap: 20, justifyContent: "center", flexWrap: "wrap",
            opacity: 0,
            animation: "floatUp 0.7s ease forwards",
            animationDelay: "1s",
          }}>
            <a href="https://discord.gg/aEPQ6Zfvhd" target="_blank" rel="noopener noreferrer" className="glow-btn">Join Discord</a>
            <a href="https://robertsspaceindustries.com/en/orgs/starkeeper" target="_blank" rel="noopener noreferrer" className="glow-btn-alt">View on RSI</a>
          </div>
        </div>

        {/* Scroll hint */}
        <div style={{
          position: "absolute", bottom: 36, left: "50%", transform: "translateX(-50%)",
          zIndex: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
          opacity: 0, animation: "floatUp 0.6s ease forwards 1.4s",
        }}>
          {/* marginRight: -3 cancels the trailing letter-spacing so the visual width matches the box width exactly,
              letting flex centering put the visual letters dead-center over the vertical tick below. */}
          <span style={{ fontFamily: "'Space Mono'", fontSize: 9, letterSpacing: 3, marginRight: -3, color: "rgba(255,255,255,0.3)" }}>SCROLL</span>
          <div style={{ width: 1, height: 40, background: "linear-gradient(to bottom, rgba(252,214,87,0.6), transparent)", animation: "pulse 2s infinite" }} />
        </div>
      </section>

      {/* STATS */}
      <section style={{
        borderTop: "1px solid rgba(252,214,87,0.2)",
        borderBottom: "1px solid rgba(252,214,87,0.2)",
        background: "rgba(252,214,87,0.04)",
        padding: "48px clamp(16px, 4vw, 40px)",
      }}>
        <div style={{
          maxWidth: 1000, margin: "0 auto",
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "clamp(12px, 2vw, 24px)", textAlign: "center",
        }}>
          {stats ? [
            { label: "Members", countTo: stats.members, prefix: "", suffix: "", special: null },
            { label: "Teams", countTo: stats.teams, prefix: "", suffix: "", special: null },
            { label: "Ships", countTo: stats.ships, prefix: "", suffix: "", special: null },
            { label: "Cumulative VC Hours · 30d", countTo: stats.vcHoursLast30d, prefix: "", suffix: "", special: null },
          ].map((s, i) => (
            <CountUpStat key={s.label} stat={s} delay={i * 0.15} />
          )) : (
            <div style={{ gridColumn: "1 / -1", fontFamily: "'Orbitron', sans-serif", fontSize: 11, letterSpacing: 3, color: "rgba(252,214,87,0.4)", padding: 20, textAlign: "center" }}>LOADING...</div>
          )}
        </div>
      </section>

      {/* MISSION */}
      <section style={{ padding: "clamp(60px, 12vw, 120px) clamp(16px, 4vw, 40px)", maxWidth: 900, margin: "0 auto" }}>
        <RevealSection>
          <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 32 }}>
            <div style={{
              width: 80, height: 2, background: "#fcd657",
              animation: "lineExpand 0.8s ease",
            }} />
            <span style={{
              fontFamily: "'Space Mono', monospace",
              fontSize: 10, letterSpacing: 4, color: "#fcd657", textTransform: "uppercase",
            }}>Our Mission</span>
          </div>
          <h2 style={{
            fontFamily: "'Orbitron', sans-serif",
            fontWeight: 700,
            fontSize: "clamp(28px, 5vw, 52px)",
            lineHeight: 1.15, marginBottom: 32, color: "#e8eaf6",
          }}>
            Explore Boldly.<br />
            <span style={{ color: "rgba(232,234,246,0.35)" }}>Operate Together.</span><br />
            Enjoy the Chaos.
          </h2>
          <p style={{
            fontFamily: "'Rajdhani', sans-serif",
            fontSize: "clamp(16px, 2vw, 21px)",
            color: "rgba(232,234,246,0.65)",
            lineHeight: 1.8, maxWidth: 680,
          }}>
            We're building a strong, supportive, and hilarious community of players who thrive in
            all corners of the 'verse — from mining rocks and hauling cargo to jumping into
            firefights and forgetting to bring medpens.
          </p>
        </RevealSection>
      </section>

      {/* DIVISIONS */}
      <section style={{ padding: "clamp(40px, 8vw, 80px) clamp(16px, 4vw, 40px) clamp(60px, 12vw, 120px)", background: "rgba(255,255,255,0.015)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <RevealSection>
            <div style={{ textAlign: "center", marginBottom: 72 }}>
              <div style={{
                fontFamily: "'Space Mono', monospace",
                fontSize: 10, letterSpacing: 5, color: "#fcd657",
                marginBottom: 16, textTransform: "uppercase",
              }}>Two Divisions</div>
              <h2 style={{
                fontFamily: "'Orbitron', sans-serif",
                fontWeight: 700,
                fontSize: "clamp(26px, 4vw, 44px)",
                color: "#e8eaf6",
              }}>Built for the Whole 'Verse</h2>
            </div>
          </RevealSection>

          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(340px, 100%), 1fr))", gap: 28,
          }}>
            {divisions.map((d, i) => (
              <RevealSection key={d.name} delay={i * 0.15} style={{ height: '100%' }}>
                <div
                  className="div-card corner-accent"
                  style={{
                    borderColor: hoveredDiv === d.name ? d.color : "rgba(255,255,255,0.08)",
                    background: hoveredDiv === d.name ? `rgba(${d.color.slice(1).match(/../g).map(x=>parseInt(x,16)).join(',')},0.07)` : "rgba(255,255,255,0.03)",
                    boxShadow: hoveredDiv === d.name ? `0 0 60px ${d.color}22` : "none",
                  }}
                  onMouseEnter={() => setHoveredDiv(d.name)}
                  onMouseLeave={() => setHoveredDiv(null)}
                >
                  <div style={{ fontSize: 40, marginBottom: 16 }}>{d.icon}</div>
                  <div style={{
                    fontFamily: "'Space Mono', monospace",
                    fontSize: 10, letterSpacing: 4,
                    color: d.color, marginBottom: 8, textTransform: "uppercase",
                  }}>Division</div>
                  <h3 style={{
                    fontFamily: "'Orbitron', sans-serif",
                    fontSize: 26, fontWeight: 700, color: "#e8eaf6", marginBottom: 6,
                  }}>{d.name}</h3>
                  <p style={{
                    fontFamily: "'Rajdhani', sans-serif",
                    fontSize: 18, fontWeight: 600, color: d.color, marginBottom: 20, fontStyle: "italic",
                  }}>{d.tagline}</p>
                  <p style={{
                    fontFamily: "'Rajdhani', sans-serif",
                    fontSize: 16, color: "rgba(232,234,246,0.6)", lineHeight: 1.7, marginBottom: 24,
                  }}>{d.desc}</p>
                  <div className="div-teams" style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {d.teams.map((t) => {
                      const teamColor = t.color || d.color;
                      const teamSlug = slugify(t.name);
                      return hasMemberRole ? (
                        <Link key={t.id || t.name} to={`/teams/${teamSlug}`} style={{
                          fontFamily: "'Space Mono', monospace",
                          fontSize: 9, letterSpacing: 1.5,
                          padding: "4px 10px",
                          border: `1px solid ${teamColor}55`,
                          color: teamColor, opacity: 0.8,
                          textTransform: "uppercase",
                          textDecoration: "none",
                          transition: "all 0.2s",
                          cursor: "pointer",
                        }}
                          onMouseEnter={e => { e.currentTarget.style.background = `${teamColor}22`; e.currentTarget.style.borderColor = `${teamColor}aa`; e.currentTarget.style.opacity = "1"; }}
                          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = `${teamColor}55`; e.currentTarget.style.opacity = "0.8"; }}
                        >{t.name}</Link>
                      ) : (
                        <span key={t.id || t.name} style={{
                          fontFamily: "'Space Mono', monospace",
                          fontSize: 9, letterSpacing: 1.5,
                          padding: "4px 10px",
                          border: `1px solid ${teamColor}55`,
                          color: teamColor, opacity: 0.8,
                          textTransform: "uppercase",
                        }}>{t.name}</span>
                      );
                    })}
                  </div>
                </div>
              </RevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* WHEN WE'RE ACTIVE — recruitment heatmap. Sits above the CTA so prospective
          members can quickly check whether the org's active hours overlap with theirs. */}
      <section style={{ padding: "clamp(60px, 10vw, 100px) clamp(16px, 4vw, 40px) clamp(20px, 4vw, 40px)", position: "relative" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <RevealSection style={{ textAlign: "center", marginBottom: 40 }}>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 5, color: "#fcd657", marginBottom: 16, textTransform: "uppercase" }}>
              When We're Active
            </div>
            <h2 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: "clamp(26px, 4vw, 44px)", color: "#e8eaf6", lineHeight: 1.1, marginBottom: 14 }}>
              Find Your Crew On Your Schedule
            </h2>
            <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: "clamp(15px, 1.6vw, 18px)", color: "rgba(232,234,246,0.55)", maxWidth: 640, margin: "0 auto", lineHeight: 1.7 }}>
              The brighter the cell, the more time Starkeepers spend together in voice. Pick your timezone — if our peak hours overlap with yours, you'll always have someone to fly with.
            </p>
          </RevealSection>
          <RevealSection delay={0.2}>
            <ActiveHoursHeatmap />
          </RevealSection>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: "clamp(60px, 12vw, 120px) clamp(16px, 4vw, 40px)", position: "relative", overflow: "hidden" }}>
        <div style={{
          position: "absolute", inset: 0,
          background: "radial-gradient(ellipse at center, rgba(252,214,87,0.06) 0%, transparent 70%)",
          pointerEvents: "none",
        }} />
        <RevealSection style={{ textAlign: "center", position: "relative", zIndex: 1 }}>
          <div style={{
            fontFamily: "'Space Mono', monospace",
            fontSize: 10, letterSpacing: 5,
            color: "#fcd657", marginBottom: 20, textTransform: "uppercase",
          }}>Ready to Join?</div>
          <h2 style={{
            fontFamily: "'Orbitron', sans-serif",
            fontWeight: 900,
            fontSize: "clamp(32px, 6vw, 72px)",
            color: "#e8eaf6", marginBottom: 16, lineHeight: 1.05,
          }}>
            Space Is Cold.<br />
            <span style={{ color: "#fcd657" }}>We're Not.</span>
          </h2>
          <p style={{
            fontFamily: "'Rajdhani', sans-serif",
            fontSize: 19, color: "rgba(232,234,246,0.5)",
            maxWidth: 480, margin: "0 auto 48px", lineHeight: 1.7,
          }}>
            Join our Discord, find your squad, and make some questionable decisions with good company.
          </p>
          <div style={{ display: "flex", gap: 20, justifyContent: "center", flexWrap: "wrap" }}>
            <a href="https://discord.gg/aEPQ6Zfvhd" target="_blank" rel="noopener noreferrer" className="glow-btn">Join Discord →</a>
            <a href="https://robertsspaceindustries.com/en/orgs/STARKEEPER" target="_blank" rel="noopener noreferrer" className="glow-btn-alt">RSI Org Page</a>
          </div>
        </RevealSection>
      </section>

      {/* FOOTER */}
      <footer style={{
        borderTop: "1px solid rgba(255,255,255,0.07)",
        padding: "40px clamp(16px, 4vw, 40px)",
        background: "rgba(0,0,0,0.3)",
      }}>
        <div style={{
          maxWidth: 1100, margin: "0 auto",
          display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 20,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <img
              src="/images/logo.png"
              alt="Starkeeper"
              style={{ height: 28, width: "auto" }}
              onError={(e) => { e.target.style.display = "none"; }}
            />
            <span style={{
              fontFamily: "'Orbitron', sans-serif",
              fontWeight: 700, fontSize: 13, letterSpacing: 2.5, color: "rgba(232,234,246,0.5)",
            }}>STARKEEPER INDUSTRIES</span>
          </div>
          <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
            {NAV_LINKS.map((l) => (
              l.external
                ? <a key={l.label} href={l.href} style={{
                    fontFamily: "'Space Mono', monospace",
                    fontSize: 10, letterSpacing: 2,
                    color: "rgba(232,234,246,0.35)",
                    transition: "color 0.2s",
                    textTransform: "uppercase",
                  }}
                    onMouseEnter={e => e.target.style.color = "#fcd657"}
                    onMouseLeave={e => e.target.style.color = "rgba(232,234,246,0.35)"}
                  >{l.label}</a>
                : <Link key={l.label} to={l.href} style={{
                    fontFamily: "'Space Mono', monospace",
                    fontSize: 10, letterSpacing: 2,
                    color: "rgba(232,234,246,0.35)",
                    transition: "color 0.2s",
                    textTransform: "uppercase",
                  }}
                    onMouseEnter={e => e.target.style.color = "#fcd657"}
                    onMouseLeave={e => e.target.style.color = "rgba(232,234,246,0.35)"}
                  >{l.label}</Link>
            ))}
          </div>
        </div>
        <div style={{
          maxWidth: 1100, margin: "24px auto 0",
          fontFamily: "'Space Mono', monospace",
          fontSize: 9, letterSpacing: 1.5,
          color: "rgba(232,234,246,0.2)",
          borderTop: "1px solid rgba(255,255,255,0.05)", paddingTop: 20,
          lineHeight: 1.8,
        }}>
          © 2026 Starkeeper Industries. All rights reserved. · This is an unofficial fansite. Star Citizen®, Squadron 42®, Roberts Space Industries®, and Cloud Imperium® are registered trademarks of Cloud Imperium Rights LLC. Not affiliated with Cloud Imperium or Roberts Space Industries.
        </div>
      </footer>
    </div>
  );
}
